import React, { useEffect } from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Textarea,
    Flex,
    Input,
    Icon,
    Badge,
} from '@chakra-ui/react';
import { DialogRoot, DialogContent } from '../../ui/dialog';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import { FaEye } from 'react-icons/fa';
import ButtonBox from '../buttons/ButtonBox';
import SelectBox from '../fields/SelectBox';
import { selectTemplate, resetEmailState, setEmailData, clearTemplate, setValidationErrors, clearValidationErrors } from '../../../store/emailSlice';
import { loadCompanyInfoRequest } from '../../../store/companyInfoSlice';
import { useColorModeValue } from '../../ui/color-mode';
import { validateEmailForm, hasValidationErrors } from '../../../utils/emailValidation';

const SendEmailDialog = ({ isOpen, onClose, recipientEmail, complaintId, complaintData }) => {
    const dispatch = useDispatch();
    const { templates, selectedTemplate, emailData, validationErrors } = useSelector(state => state.email);
    const companyInfo = useSelector(state => state.companyInfo?.info || {});
    const { control, reset } = useForm({
        defaultValues: {
            emailTemplate: ''
        }
    });
    const bgColor = useColorModeValue('white', 'gray.800');
    const borderColor = useColorModeValue('gray.200', 'gray.600');
    const textColor = useColorModeValue('gray.700', 'gray.200');
    const labelColor = useColorModeValue('gray.600', 'gray.400');
    const inputBg = useColorModeValue('white', 'gray.700');
    const headerBg = useColorModeValue('blue.600', 'blue.700');
    const errorColor = useColorModeValue('red.500', 'red.400');

    // Reset form when dialog opens
    useEffect(() => {
        if (isOpen) {
            reset({ emailTemplate: '' });
            dispatch(selectTemplate(null));
            // Set recipient email from parent if provided
            if (recipientEmail) {
                dispatch(setEmailData({
                    to: recipientEmail,
                    subject: '',
                    body: ''
                }));
            }
            dispatch(clearValidationErrors());
            dispatch(loadCompanyInfoRequest());
        }
    }, [isOpen, recipientEmail, reset, dispatch]);

    const handleTemplateSelect = (templateId) => {
        if (!templateId || (Array.isArray(templateId) && templateId.length === 0)) return;

        const id = Array.isArray(templateId) ? parseInt(templateId[0]) : parseInt(templateId);
        dispatch(selectTemplate(id));

        // Auto-fill subject and body from template
        const template = templates.find(t => t.id === id);
        if (template) {
            dispatch(setEmailData({
                subject: template.subject || '',
                body: template.body || ''
            }));
        }
        dispatch(clearValidationErrors());
    };    // Watch selectbox value and dispatch selection when changed
    const watchedTemplate = useWatch({ control, name: 'emailTemplate' });
    useEffect(() => {
        if (watchedTemplate && (!Array.isArray(watchedTemplate) || watchedTemplate.length > 0)) {
            handleTemplateSelect(watchedTemplate);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [watchedTemplate, templates]);

    const handleBodyChange = (e) => {
        dispatch(setEmailData({ body: e.target.value }));
    };

    const handleSubjectChange = (e) => {
        dispatch(setEmailData({ subject: e.target.value }));
    };

    const handleResetTemplate = () => {
        dispatch(clearTemplate());
        reset({ emailTemplate: [] });
        dispatch(clearValidationErrors());
    };

    const handleClose = () => {
        dispatch(resetEmailState());
        onClose();
    };

    const handleSendEmail = () => {
        // Validate form before sending
        const errors = validateEmailForm(emailData);
        if (hasValidationErrors(errors)) {
            dispatch(setValidationErrors(errors));
            return;
        }

        // Clear any previous validation errors
        dispatch(clearValidationErrors());

        // Format subject and body with actual values
        const formattedSubject = formatContent(emailData.subject);
        const formattedBody = formatContent(emailData.body);

        dispatch({
            type: "SEND_EMAIL",
            payload: {
                complaintId: complaintId,
                email: complaintData?.email,
                subject: formattedSubject,
                body: formattedBody,
                companyName: companyInfo?.name || ''
            }
        });
        handleClose();
    };

    // Format body with complaint data and company info
    const formatContent = (content) => {
        if (!content) return '';
        let formatted = content;
        formatted = formatted.replace(/{companyName}/g, companyInfo?.name || 'Support Team');
        formatted = formatted.replace(/{customerName}/g, complaintData?.customerName || 'Customer');
        formatted = formatted.replace(/{complaintId}/g, complaintId);
        formatted = formatted.replace(/{productName}/g, complaintData?.productName || 'Product');
        return formatted;
    };

    const templateOptions = templates.map(template => ({
        label: template.name,
        value: template.id.toString(),
        description: template.description
    }));

    const isFormValid = !hasValidationErrors(validationErrors);

    return (
        <>
            <DialogRoot open={isOpen} onOpenChange={({ open }) => !open && handleClose()}>
            <DialogContent
                maxWidth="2xl"
                bg={bgColor}
                borderRadius="lg"
                placement="center"
                mt="120px"
                p={0}
                overflow="hidden"
            >
                {/* Header */}
                <Box bg={headerBg} color="white" p={6}>
                    <HStack spacing={3}>
                        <Icon as={FaEye} boxSize={5} />
                        <VStack align="start" spacing={0}>
                            <Text fontSize="lg" fontWeight="bold">
                                Send Email to Customer
                            </Text>
                            <Text fontSize="sm" opacity={0.9}>
                                Template-based professional email system
                            </Text>
                        </VStack>
                    </HStack>
                </Box>

                {/* Body */}
                <Flex height="500px" bg={bgColor}>
                    <Box flex={1} p={6} borderRight="1px solid" borderColor={borderColor} overflowY="auto">
                        <VStack spacing={6} align="stretch">
                            {/* Template Selection */}
                            <Box>
                                <Badge colorScheme="green" mb={2}>
                                    Recommended
                                </Badge>
                                <SelectBox
                                    control={control}
                                    name="emailTemplate"
                                    label="Choose Email Template"
                                    placeholder="Select a template"
                                    items={templateOptions}
                                    onChange={handleTemplateSelect}
                                />
                            </Box>

                            {/* Subject */}
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={2} color={labelColor}>
                                    Subject:
                                </Text>
                                <Input
                                    type="text"
                                    value={formatContent(emailData.subject)}
                                    onChange={handleSubjectChange}
                                    bg={inputBg}
                                    borderColor={validationErrors.subject ? errorColor : borderColor}
                                    color={textColor}
                                    fontSize="sm"
                                    placeholder="Email subject"
                                />
                                {validationErrors.subject && (
                                    <Text fontSize="xs" color={errorColor} mt={1}>
                                        {validationErrors.subject}
                                    </Text>
                                )}
                            </Box>

                            {/* Message */}
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={2} color={labelColor}>
                                    Message:
                                </Text>
                                <Textarea
                                    value={formatContent(emailData.body)}
                                    onChange={handleBodyChange}
                                    minHeight="250px"
                                    borderRadius="md"
                                    border="1px solid"
                                    borderColor={validationErrors.body ? errorColor : borderColor}
                                    bg={inputBg}
                                    color={textColor}
                                    fontSize="sm"
                                    placeholder="Email message"
                                    resize="vertical"
                                    _focus={{
                                        borderColor: 'blue.400',
                                        boxShadow: '0 0 0 1px blue.400',
                                    }}
                                />
                                {validationErrors.body && (
                                    <Text fontSize="xs" color={errorColor} mt={1}>
                                        {validationErrors.body}
                                    </Text>
                                )}
                            </Box>
                        </VStack>
                    </Box>
                </Flex>

                {/* Footer */}
                <Flex
                    p={6}
                    borderTop="1px solid"
                    borderColor={borderColor}
                    justify="space-between"
                    gap={3}
                    bg={bgColor}
                >
                    <ButtonBox
                        name="Reset Template"
                        variant="outline"
                        colorScheme="orange"
                        onClick={handleResetTemplate}
                        disabled={!selectedTemplate}
                    />
                    <HStack gap={3}>
                        <ButtonBox
                            name="Cancel"
                            variant="outline"
                            colorScheme="blue"
                            onClick={handleClose}
                        />
                        <ButtonBox
                            name="Send Email"
                            colorPalette="green"
                            variant="solid"
                            onClick={handleSendEmail}
                            disabled={!isFormValid}
                        />
                    </HStack>
                </Flex>
            </DialogContent>
        </DialogRoot>
    </>
    );
};

export default SendEmailDialog;
